import React from 'react'
import { Link } from 'react-router-dom'
import { Form } from 'react-final-form'
import _ from 'lodash'
import { Container, Box, Typography, Grid, Button, CircularProgress } from '@mui/material'
import { TextField, Switches, Checkboxes, Autocomplete } from 'mui-rff'
import arrayMutators from 'final-form-arrays'
import { FieldArray } from 'react-final-form-arrays'
import { OnChange } from 'react-final-form-listeners'
import { AddCircleRounded, CancelRounded } from '@mui/icons-material'
import { CS_ROLE, DEALER_ROLE, SALES_ROLE, ROLES } from '../../constants/roles'
import htgCompanies, { HTG_COMPANIES } from '../../constants/companies'

const AdminUserForm = ({
  initialValues,
  accounts,
  salesIds,
  submitHandler,
  onSubmit,
  title,
  submitButtonText,
  disableEmail = false,
  isLoadingAccount,
  isLoadingSales,
}) => {
  return (
    <Container>
      <Box paddingY='0.5rem' marginBottom='1rem'>
        <Typography variant='h6'>{title}</Typography>
      </Box>
      <Form
        onSubmit={submitHandler}
        initialValues={initialValues}
        mutators={{ ...arrayMutators }}
        render={({ handleSubmit, submitting, values, form }) => {
          const isDealer = values.role === DEALER_ROLE
          const isSales = values.role === SALES_ROLE
          const isCS = values.role === CS_ROLE

          const companiesFieldTitle = isDealer
            ? 'Company Account Number'
            : 'Accessible HTG Companies'

          const companiesFieldSubtitle = isDealer ? 'Account' : null

          const companiesAddText = isDealer ? 'Add Account' : 'Add HTG Company'

          const htgAppendText = isCS ? ' - All documents' : ''

          return (
            <form onSubmit={onSubmit(handleSubmit, form)}>
              <Box sx={{ flexGrow: 1 }} paddingY='0.5rem' maxWidth='500px'>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box>
                      <Typography fontWeight='500' marginBottom='0.5rem'>
                        Email
                      </Typography>
                      <TextField
                        type='email'
                        name='email'
                        margin='none'
                        required={true}
                        size='small'
                        placeholder='example@hotmail.com'
                        disabled={disableEmail}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box>
                      <Typography fontWeight='500' marginBottom='0.5rem'>
                        Role
                      </Typography>
                      <Autocomplete
                        fullWidth
                        name={'role'}
                        size='small'
                        required={true}
                        textFieldProps={{
                          variant: 'outlined',
                          margin: 'none',
                          placeholder: 'Role *',
                        }}
                        options={ROLES}
                        getOptionLabel={(role) => role?.toUpperCase()}
                        disableClearable
                      />
                      <OnChange name='role'>
                        {(value) => {
                          if (value !== DEALER_ROLE) {
                            form.change(
                              'companies',
                              _.map(values.companies, ({ htg, allDocuments }) => ({
                                htg,
                                allDocuments,
                              })),
                            )
                          }
                        }}
                      </OnChange>
                    </Box>
                  </Grid>
                  <Grid item xs={12} display={isSales ? '' : 'none'}>
                    <FieldArray name='salesIds'>
                      {({ fields }) => (
                        <Box>
                          <Typography fontWeight='500' marginBottom='0.5rem'>
                            Sales Id
                          </Typography>
                          {fields.map((name, index) => (
                            <Box key={index} paddingBottom='0.3rem'>
                              <Autocomplete
                                fullWidth
                                name={name}
                                size='small'
                                required={true}
                                textFieldProps={{
                                  variant: 'outlined',
                                  margin: 'none',
                                  placeholder: 'Sales Id *',
                                  InputProps: {
                                    endAdornment: (
                                      <>{isLoadingSales ? <CircularProgress size={20} /> : null}</>
                                    ),
                                  },
                                }}
                                options={salesIds || []}
                                disableClearable
                              />
                              <Button
                                size='small'
                                onClick={() => fields.remove(index)}
                                startIcon={<CancelRounded style={{ color: 'red' }} />}
                                disabled={values.salesIds.length === 1}
                              >
                                Remove
                              </Button>
                            </Box>
                          ))}
                          <Button
                            size='small'
                            onClick={() => fields.push('')}
                            startIcon={<AddCircleRounded color='primary' />}
                          >
                            Add Sales
                          </Button>
                        </Box>
                      )}
                    </FieldArray>
                    <OnChange name='role'>
                      {(value) => {
                        if (value !== SALES_ROLE) {
                          form.change('salesIds', [])
                        } else {
                          form.change(
                            'salesIds',
                            initialValues.salesIds?.length > 0 ? initialValues.salesIds : [null],
                          )
                        }
                        form.change(
                          'companies',
                          initialValues.companies?.length > 0 ? initialValues.companies : [{}],
                        )
                      }}
                    </OnChange>
                  </Grid>
                  <Grid item xs={12}>
                    <FieldArray name='companies'>
                      {({ fields }) => (
                        <Box>
                          <Typography fontWeight='500' marginBottom='0.5rem'>
                            {companiesFieldTitle}
                          </Typography>

                          {fields.map((name, index) => {
                            const selectedHTG = values.companies?.[index]?.htg
                            const accountOptions = accounts?.[selectedHTG] || []

                            return (
                              <Box key={name}>
                                {companiesFieldSubtitle && (
                                  <Typography>{`${companiesFieldSubtitle} ${
                                    index + 1
                                  }`}</Typography>
                                )}
                                <Box paddingBottom='0.3rem'>
                                  <Autocomplete
                                    fullWidth
                                    name={`${name}.htg`}
                                    size='small'
                                    required={true}
                                    textFieldProps={{
                                      variant: 'outlined',
                                      margin: 'none',
                                      placeholder: 'HTG Company *',
                                    }}
                                    options={HTG_COMPANIES}
                                    getOptionLabel={(htg) =>
                                      htg ? htgCompanies[htg].shortName + htgAppendText : ''
                                    }
                                    disableClearable
                                  />
                                </Box>
                                <Box display={isSales ? '' : 'none'}>
                                  <Checkboxes
                                    name={`${name}.allDocuments`}
                                    data={{
                                      label: 'All documents (bypass Sales Id)',
                                      value: true,
                                    }}
                                  />
                                </Box>
                                <Box paddingBottom='0.3rem' display={isDealer ? '' : 'none'}>
                                  <Autocomplete
                                    fullWidth
                                    name={`${name}.customerId`}
                                    size='small'
                                    required={isDealer}
                                    textFieldProps={{
                                      variant: 'outlined',
                                      margin: 'none',
                                      placeholder: 'Company Id *',
                                      InputProps: {
                                        endAdornment: (
                                          <>
                                            {isLoadingAccount ? (
                                              <CircularProgress size={20} />
                                            ) : null}
                                          </>
                                        ),
                                      },
                                    }}
                                    options={accountOptions}
                                    getOptionLabel={(account) =>
                                      account ? `${account.name} (${account.customerId})` : ''
                                    }
                                    onChange={(event, newValue) =>
                                      newValue &&
                                      form.change(`${name}.customerId`, newValue.customerId)
                                    }
                                    getOptionValue={(account) => account.customerId}
                                    value={
                                      accountOptions.find(
                                        (item) =>
                                          item.customerId === values.companies?.[index]?.customerId,
                                      ) || null
                                    }
                                    disableClearable
                                  />
                                </Box>
                                <Button
                                  size='small'
                                  onClick={() => fields.remove(index)}
                                  startIcon={<CancelRounded style={{ color: 'red' }} />}
                                  disabled={values.companies.length === 1}
                                >
                                  Remove
                                </Button>
                              </Box>
                            )
                          })}
                          <Button
                            size='small'
                            onClick={() => fields.push({ htg: '', customerId: '' })}
                            startIcon={<AddCircleRounded color='primary' />}
                          >
                            {companiesAddText}
                          </Button>
                        </Box>
                      )}
                    </FieldArray>
                  </Grid>
                  <hr />
                  <Grid item xs={12}>
                    <Box>
                      <Switches
                        name='configs'
                        formControlProps={{ margin: 'none' }}
                        data={[
                          { label: 'Is Admin', value: 'isAdmin' },
                          { label: 'Is Active', value: 'isActive' },
                        ]}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} textAlign='left'>
                    <Button variant='contained' component={Link} to='/admin' size='small'>
                      Back
                    </Button>
                  </Grid>
                  <Grid item xs={6} textAlign='right'>
                    <Button
                      disabled={submitting}
                      type='submit'
                      variant='contained'
                      color='primary'
                      size='small'
                    >
                      {submitButtonText}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </form>
          )
        }}
      />
    </Container>
  )
}

export default AdminUserForm
